import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  QrCode,
  Copy,
  Check,
  Download,
  RefreshCw,
  Clock,
  CheckCircle2,
  XCircle,
  Trash2,
  Eye,
  Plus,
  DollarSign,
  FileText,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const QRPaymentGenerator = () => {
  const { isDarkMode } = useOutletContext() || {};
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [copied, setCopied] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'LRD',
    description: '',
    expires_in_hours: 24
  });

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/merchant/qr-payments?limit=50`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPayments(data);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQR = async (e) => {
    e.preventDefault();
    setGenerating(true);
    
    try {
      const payload = {
        currency: formData.currency,
        expires_in_hours: parseInt(formData.expires_in_hours)
      };
      
      if (formData.amount) {
        payload.amount = parseFloat(formData.amount);
      }
      if (formData.description) {
        payload.description = formData.description;
      }
      
      const res = await fetch(`${API_URL}/api/merchant/qr-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`QR Payment created: ${data.payment.reference}`);
        setSelectedPayment(data.payment);
        setShowForm(false);
        setFormData({ amount: '', currency: 'LRD', description: '', expires_in_hours: 24 });
        fetchPayments();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to generate QR code');
      }
    } catch (error) {
      toast.error('Failed to generate QR code');
    } finally {
      setGenerating(false);
    }
  };

  const handleCancelPayment = async (paymentId) => {
    try {
      const res = await fetch(`${API_URL}/api/merchant/qr-payments/${paymentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Payment cancelled');
        fetchPayments();
        if (selectedPayment?.payment_id === paymentId) {
          setSelectedPayment(null);
        }
      }
    } catch (error) {
      toast.error('Failed to cancel payment');
    }
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(null), 2000);
  };

  const downloadQRCode = (base64Data, reference) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${base64Data}`;
    link.download = `FlowLedger-QR-${reference}.png`;
    link.click();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3" /> Pending
          </span>
        );
      case 'confirmed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle2 className="w-3 h-3" /> Confirmed
          </span>
        );
      case 'expired':
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            <Clock className="w-3 h-3" /> Expired
          </span>
        );
      case 'cancelled':
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <XCircle className="w-3 h-3" /> Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="qr-payment-generator">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
            QR Payment Generator
          </h1>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Generate QR codes for easy payment collection
          </p>
        </div>
        <button
          onClick={() => { setShowForm(true); setSelectedPayment(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-all duration-200"
          data-testid="generate-qr-btn"
        >
          <Plus className="w-4 h-4" />
          Generate QR
        </button>
      </div>

      <div className="flex-1 flex gap-6 overflow-hidden">
        {/* Left Panel - QR Code Display / Form */}
        <div className={`w-1/2 rounded-xl p-6 flex flex-col ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
          {showForm ? (
            // Generate QR Form
            <div className="flex-1">
              <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                Create New QR Payment
              </h2>
              
              <form onSubmit={handleGenerateQR} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Amount (Optional)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="Leave empty for any amount"
                      className={`flex-1 px-3 py-2 rounded-lg border ${
                        isDarkMode
                          ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                          : 'bg-white border-gray-300 text-slate-800 placeholder-slate-400'
                      }`}
                    />
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className={`w-24 px-3 py-2 rounded-lg border ${
                        isDarkMode
                          ? 'bg-slate-700 border-slate-600 text-white'
                          : 'bg-white border-gray-300 text-slate-800'
                      }`}
                    >
                      <option value="LRD">LRD</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Description (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., Invoice #123, Product purchase"
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDarkMode
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                        : 'bg-white border-gray-300 text-slate-800 placeholder-slate-400'
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Expires In
                  </label>
                  <select
                    value={formData.expires_in_hours}
                    onChange={(e) => setFormData({ ...formData, expires_in_hours: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDarkMode
                        ? 'bg-slate-700 border-slate-600 text-white'
                        : 'bg-white border-gray-300 text-slate-800'
                    }`}
                  >
                    <option value="1">1 hour</option>
                    <option value="6">6 hours</option>
                    <option value="12">12 hours</option>
                    <option value="24">24 hours</option>
                    <option value="48">48 hours</option>
                    <option value="168">7 days</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className={`flex-1 px-4 py-2 rounded-lg ${
                      isDarkMode
                        ? 'bg-slate-700 text-white hover:bg-slate-600'
                        : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={generating}
                    className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {generating ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <QrCode className="w-4 h-4" />
                        Generate QR Code
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          ) : selectedPayment ? (
            // QR Code Display
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className={`p-4 rounded-2xl ${isDarkMode ? 'bg-white' : 'bg-gray-50'}`}>
                <img
                  src={`data:image/png;base64,${selectedPayment.qr_code_data}`}
                  alt="QR Code"
                  className="w-64 h-64"
                />
              </div>
              
              <div className="mt-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className={`text-lg font-mono font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                    {selectedPayment.reference}
                  </span>
                  <button
                    onClick={() => copyToClipboard(selectedPayment.reference, 'ref')}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    {copied === 'ref' ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
                
                {selectedPayment.amount ? (
                  <p className="text-2xl font-bold text-teal-500">
                    {selectedPayment.currency} {selectedPayment.amount.toLocaleString()}
                  </p>
                ) : (
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Any amount
                  </p>
                )}
                
                {selectedPayment.description && (
                  <p className={`mt-1 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {selectedPayment.description}
                  </p>
                )}
                
                <div className="mt-3">
                  {getStatusBadge(selectedPayment.status)}
                </div>
                
                {selectedPayment.status === 'confirmed' && (
                  <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
                    <p className="text-green-600 font-medium">
                      Payment Received: {selectedPayment.currency} {selectedPayment.confirmed_amount?.toLocaleString()}
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                      From: {selectedPayment.sender_number} via {selectedPayment.sender_network}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => downloadQRCode(selectedPayment.qr_code_data, selectedPayment.reference)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                    isDarkMode
                      ? 'bg-slate-700 text-white hover:bg-slate-600'
                      : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
                  }`}
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                
                <button
                  onClick={() => {
                    const paymentUrl = `${window.location.origin}/pay/${selectedPayment.reference}`;
                    copyToClipboard(paymentUrl, 'url');
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                    isDarkMode
                      ? 'bg-slate-700 text-white hover:bg-slate-600'
                      : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
                  }`}
                >
                  {copied === 'url' ? <Check className="w-4 h-4" /> : <ExternalLink className="w-4 h-4" />}
                  Copy Link
                </button>
                
                {selectedPayment.status === 'pending' && (
                  <button
                    onClick={() => handleCancelPayment(selectedPayment.payment_id)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                  >
                    <Trash2 className="w-4 h-4" />
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ) : (
            // Empty State
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className={`p-6 rounded-2xl ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <QrCode className={`w-24 h-24 ${isDarkMode ? 'text-slate-500' : 'text-gray-300'}`} />
              </div>
              <p className={`mt-4 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Generate a QR code or select a payment<br />from the list to preview
              </p>
              <button
                onClick={() => setShowForm(true)}
                className="mt-4 flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
              >
                <Plus className="w-4 h-4" />
                Generate New QR
              </button>
            </div>
          )}
        </div>

        {/* Right Panel - Payment History */}
        <div className={`w-1/2 rounded-xl flex flex-col ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
          <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
            <h2 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
              QR Payment History
            </h2>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            {payments.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <FileText className={`w-12 h-12 mb-3 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                  No QR payments yet
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {payments.map((payment, index) => (
                  <div
                    key={payment.payment_id}
                    onClick={() => { setSelectedPayment(payment); setShowForm(false); }}
                    className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                      selectedPayment?.payment_id === payment.payment_id
                        ? isDarkMode
                          ? 'bg-teal-900/30 border-teal-500'
                          : 'bg-teal-50 border-teal-500'
                        : isDarkMode
                          ? 'bg-slate-700/50 border-slate-600 hover:bg-slate-700'
                          : 'bg-gray-50 border-gray-200 hover:bg-white hover:shadow-sm'
                    }`}
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`font-mono font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                        {payment.reference}
                      </span>
                      {getStatusBadge(payment.status)}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        {payment.amount ? (
                          <span className={`font-semibold ${
                            payment.status === 'confirmed' ? 'text-green-500' : (isDarkMode ? 'text-slate-300' : 'text-slate-700')
                          }`}>
                            {payment.currency} {payment.amount.toLocaleString()}
                          </span>
                        ) : (
                          <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Any amount
                          </span>
                        )}
                        {payment.description && (
                          <p className={`text-xs mt-0.5 truncate max-w-[200px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            {payment.description}
                          </p>
                        )}
                      </div>
                      
                      <div className={`text-xs text-right ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        {new Date(payment.created_at).toLocaleDateString()}
                        <br />
                        {new Date(payment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    
                    {payment.status === 'confirmed' && payment.sender_number && (
                      <div className={`mt-2 pt-2 border-t text-xs ${isDarkMode ? 'border-slate-600 text-green-400' : 'border-gray-200 text-green-600'}`}>
                        Paid by: {payment.sender_number} via {payment.sender_network}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRPaymentGenerator;
